'use client';

import { API_ROUTES, useApiQuery } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { PLAY_ROUTE, useHoverRoutePrefetch } from '@shared/hooks';
import { ROUTES } from '@shared/utils';
import Link from 'next/link';
import { HeartButtonWrapper } from '../../../../HeartButtonWrapper/HeartButtonWrapper';
import type { ProductVertical } from '../../../../PageToolbarWithNavigation/navigationConfig/types';
import { Button } from '../../../../shadcn';
import type { GameInfoType } from '../../types';

type GameResponsiveActionsProps = {
  id: string;
  game: GameInfoType;
  playButtonText: string;
  productVertical: ProductVertical;
};

/**
 * GameResponsiveActions is client component used to display the responsive actions for a game,
 *  that requires authentication to play or favourite the game.
 * @param id - The id of the game.
 * @param game - The game info object.
 * @param playButtonText - The text for the play button.
 * @param productVertical - The product vertical.
 * @returns The GameResponsiveActions component.
 */
function GameResponsiveActions({ id, game, playButtonText, productVertical }: GameResponsiveActionsProps) {
  const {
    session: { status },
  } = useSession();
  const gameRoutes = productVertical === 'casino' ? ROUTES.CASINO : ROUTES.LIVE_CASINO;
  const href = gameRoutes.GAME(id);
  const routePrefetch = useHoverRoutePrefetch({ href, isAuthenticated: status === 'authenticated', authPathMatch: PLAY_ROUTE });

  const { data: playerFavourites } = useApiQuery<{ favourites: string[] }>(API_ROUTES.PLAYER_DATA.FAVOURITES_GAMES, {
    queryOptions: { enabled: status === 'authenticated' },
    credentials: true,
  });
  const isFavourited = playerFavourites?.favourites.includes(id) ?? false;

  return (
    <div
      className="flex items-center justify-between fixed bottom-0 left-0 right-0 shadow-top-m rounded-t-lg px-5 gap-6 py-4 bg-container-base/80 flex-row-reverse md:relative md:bg-container md:py-2 md:px-4 md:rounded-md md:mb-3 md:flex-row"
      data-track-container="Actions"
      data-track-name-favourite={isFavourited ? 'yes' : 'no'}
    >
      <HeartButtonWrapper id={id} name={game.name} namespace="casino.favourite" isFavourited={isFavourited} variant="background" data-track-name="Favourite" />
      <Button variant="primary" size="m" asChild data-track-name="Play" className="w-full md:w-auto">
        <Link href={href} {...routePrefetch}>
          {playButtonText}
        </Link>
      </Button>
    </div>
  );
}

export { GameResponsiveActions };
